// storybook-check-ignore
import * as React from 'react';
import Cookies from 'js-cookie';
import { Sentry, analytics } from '@opendoor/observability';

import AccountForm from '@opendoor/cinderblocks/platform/auth/AccountForm';
import LogInConnector, { promptGoogleOneTap, AccountProps } from '../shared/AccountConnector';
import { REDIRECT_URLS_REGEX } from '../globals';
import { Flex, BoxProps } from '@opendoor/bricks/core';
import { breakpoints } from '@opendoor/bricks/theme/eero';
import useExclusiveAnalytics from './hooks/useExclusiveAnalytics';
import { isValidRelativeRedirectTo } from '../../helpers/exclusives/redirectTo';

const UNAUTHORIZED_STATUS_CODE = 401;

export interface Props extends AccountProps {
  redirectTo?: string;
  emailPrefill?: string;
  onSubmit?: any;
  boxProps?: BoxProps;
  loginHeaderText?: string;
  registrationHeaderText?: string;
  loginSubHeaderText?: string;
}

function ExclusivesAccountFormPage(props: Props) {
  const { trackExclusivesEvent } = useExclusiveAnalytics();
  function reportEvent<T>(label: string, additionalContextualData: T) {
    trackExclusivesEvent('exclusives-login', label, undefined, additionalContextualData);
  }
  // componentDidMount
  React.useEffect(() => {
    const { email, register, redirectTo, emailPrefill } = props;
    analytics.register({ login: Cookies.get('login-test') || 'no-cookie' });
    reportEvent('load', {
      email,
      redirectTo,
      emailPrefill,
    });
    promptGoogleOneTap('cosmos-exclusive-login', register);
  }, []);

  // Component will receive Props
  React.useEffect(() => {
    redirectIfLoggedIn(props);
    const { loginRequestStatus } = props;
    if (
      loginRequestStatus &&
      loginRequestStatus.error &&
      loginRequestStatus.statusCode !== UNAUTHORIZED_STATUS_CODE
    ) {
      Sentry.captureException(loginRequestStatus.error);
    }
  }, [props]);

  function trackLoginEvent(
    topic: 'google-auth' | 'facebook-auth',
    label: string,
    extra: Record<string, unknown>,
  ) {
    reportEvent(label, {
      topic,
      ...extra,
    });
  }

  function redirectIfLoggedIn(props: Props) {
    const { loggedIn, email, redirectTo, serverLoginRedirect } = props;
    if (loggedIn) {
      if (props.onSubmit) {
        props.onSubmit(email);
      } else {
        reportEvent('redirect', {
          email,
          redirectTo,
          serverLoginRedirect,
        });
        let redirectUrl;
        if (!redirectTo && serverLoginRedirect && serverLoginRedirect !== '/') {
          redirectUrl = serverLoginRedirect;
        } else if (redirectTo) {
          // allow redirection to relative urls & opendoor.com absolute urls
          if (REDIRECT_URLS_REGEX.test(redirectTo) || isValidRelativeRedirectTo(redirectTo)) {
            // absolute path
            redirectUrl = redirectTo;
          }
        }
        window.location.assign(redirectUrl || '/');
      }
    }
  }

  const {
    logIn,
    register,
    requestHumanStatus,
    hasGeneratedPassword,
    isNewHuman,
    loginRequestStatus,
    humanStatusRequestStatus,
    registrationRequestStatus,
    email,
    emailPrefill,
    requestResetPassword,
    loginSubHeaderText,
    loginHeaderText = 'Sign in or create an account',
    registrationHeaderText = 'Create an Opendoor account',
  } = props;

  return (
    <Flex
      maxWidth={breakpoints.slice(1)}
      mx="auto"
      justifyContent="center"
      mb={[11, 12]}
      mt={[0, 0, '4rem']}
      flex="1"
      height="100%"
      padding={[7, 7, 0]}
      {...props.boxProps}
    >
      <AccountForm
        analyticsName="cosmos-exclusives-account-form"
        loginSubHeaderText={loginSubHeaderText}
        loginHeaderText={loginHeaderText}
        registrationHeaderText={registrationHeaderText}
        email={email || emailPrefill}
        hasGeneratedPassword={hasGeneratedPassword}
        error={
          loginRequestStatus.error ||
          humanStatusRequestStatus.error ||
          registrationRequestStatus.error
        }
        passwordLoading={loginRequestStatus.isLoading || registrationRequestStatus.isLoading}
        emailLoading={humanStatusRequestStatus.isLoading}
        isNewHuman={isNewHuman}
        onLogin={logIn}
        overrideHasSubmittedEmailCheckForLogin={!!emailPrefill}
        onRegister={register}
        onHumanStatusRequest={requestHumanStatus}
        showGoogleAuth
        showFacebookAuth
        trackEvent={trackLoginEvent}
        requestResetPassword={requestResetPassword}
        phoneNumberInputOptional={false}
        containerProps={{ padding: 0 }}
      />
    </Flex>
  );
}

export default LogInConnector(ExclusivesAccountFormPage);
